// biome-ignore-all lint/nursery/noDanglingUnderscore: Pendo's install snippet, kept as published
// biome-ignore-all lint/style/useTemplate: Pendo's install snippet, kept as published
import snakeCase from 'lodash/snakeCase';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { authSelectors } from 'app-components/flux/auth';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { profileSelectors } from 'app-components/flux/profile';
import { getCommonConfig, getLogger, isDemoMode } from 'app-components/utils/core';

import { institutionLoginsSelectors } from 'app-components/flux/institution-logins';
import { subscriptionsSelectors } from 'app-components/flux/subscriptions';
import { userInfoSelectors } from 'app-components/flux/user-info';

const log = getLogger('utils/pendo.js');

function updatePendo({
  // profile,
  authSession,
  subscriptionTrackingProps,
  addFiDefaultProvider,
  financialInstitutions,
  currentDataset,
  userInfo,
}) {
  if (window.pendo) {
    const subscriptionPropsPendo = {};
    Object.keys(subscriptionTrackingProps).forEach((propName) => {
      subscriptionPropsPendo[snakeCase(propName)] = subscriptionTrackingProps[propName];
    });
    // Call this whenever information about your visitors becomes available
    // Please use Strings, Numbers, or Bools for value types.
    const metaData = {
      visitor: {
        id: authSession.qcsId, // Required if user is logged in
        scope: authSession.scope,
        groups: authSession.groups,
        source: userInfo?.source,
        demo_mode: isDemoMode(),
        // email: profile.username, // Recommended if using Pendo Feedback, or NPS Email
        // full_name: `${profile.firstName} ${profile.lastName}`, // Recommended if using Pendo Feedback
        // role:         // Optional

        // You can add any additional visitor level key-values here,
        // as long as it's not one of the above reserved names.
        app_version: String(process.env.APP_VERSION),
        default_aggregator: addFiDefaultProvider,
        financial_institutions: financialInstitutions?.join(', '),
        financial_institutions_list: financialInstitutions,
        ...(currentDataset && { current_dataset_created: currentDataset.createdAt }),
        ...subscriptionPropsPendo,
      },

      account: {
        // id: subscription.productLineName, // Highly recommended
        // name: subscription.productLineName, // Optional
        // is_paying: subscription.active, // Recommended if using Pendo Feedback
        // monthly_value:// Recommended if using Pendo Feedback
        // planLevel: subscription.tierName, // Optional
        // planPrice:    // Optional
        // creationDate: subscription.effectiveAt, // Optional
        // You can add any additional account level key-values here,
        // as long as it's not one of the above reserved names.
        // expireOn: subscription.expireOn,
        // ...subscriptionPropsPendo,
      },
    };
    log.debug('\npendo identify:\n', metaData);
    try {
      window.pendo.updateOptions?.(metaData);
    } catch (e) {
      assert(false, e);
    }

    // if (authSession?.accessToken) {
    //   window.pendo.loadGuides?.();
    //   window.pendo.startGuides?.(); // triggers CORS exception
    //   log.log('pendo: start');
    // } else {
    //   window.pendo.stopGuides?.();
    //   log.log('pendo: stop');
    // }
  }
}

function pendoSetup(apiKey) {
  (function (p, e, n, d, o) {
    var v, w, x, y, z;
    o = p[d] = p[d] || {};
    o._q = o._q || [];
    v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'];
    for (w = 0, x = v.length; w < x; ++w)
      (function (m) {
        o[m] =
          o[m] ||
          function () {
            o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0)));
          };
      })(v[w]);
    y = e.createElement(n);
    y.async = !0;
    y.src = 'https://cdn.pendo.io/agent/static/' + apiKey + '/pendo.js';
    z = e.getElementsByTagName(n)[0];
    z.parentNode.insertBefore(y, z);
  })(window, document, 'script', 'pendo');
}

function Pendo() {
  const featureFlagsError = useSelector(featureFlagsSelectors.getError);
  const featureFlagsLoadPending = useSelector(featureFlagsSelectors.getLoadPending);
  const pendoEnabled = useSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'pendo'));
  const addFiDefaultProvider = useSelector((state) =>
    featureFlagsSelectors.getFeatureFlag(state, 'addFiDefaultProvider'),
  );
  const profile = useSelector(profileSelectors.getProfile);
  const authSession = useSelector(authSelectors.getAuthSession);
  const subscriptionTrackingProps = useSelector((state) =>
    subscriptionsSelectors.getSubscriptionTrackingProps(state, authSession?.datasetId),
  );
  const financialInstitutions = useSelector(institutionLoginsSelectors.getFinancialInstitutionsPendo);
  const currentDataset = useSelector(authSelectors.getCurrentDataset);
  const userInfo = useSelector(userInfoSelectors.getUserInfo);

  useEffect(() => {
    if ((!featureFlagsLoadPending || featureFlagsError) && !window?.Cypress) {
      if (!window.pendo && pendoEnabled && getCommonConfig().pendo_api_key) {
        // we don't pass pendo_api_key to pendo sdk - it's hardcoded instead inside app-components/external.pendo-simplifi
        // pendo sdk code is not compatible with ES5 'strict' mode, so we can't cross pendo sdk boundary
        log.log('Init Pendo...');
        try {
          pendoSetup(getCommonConfig().pendo_api_key);
          assert(window.pendo, 'pendo init failed');
          window.pendo?.initialize?.({
            disableCookies: true, // Do not use cookies for storing any values, defaulting to in-memory storage only. Default is false.
            disablePersistence: true, // Do not use cookies to remember any Visitor identifiers or metadata, last seen Guide Step, and various other pieces of state. While this will prevent the Agent writing cookies, it will also ignore any identifiers already stored in cookies and may produce an increase of anonymous Visitors in Pendo, depending on how soon your application identifies the Visitor. Default is false.
            // 'guides.delay': true, // Guides will be loaded on initialize but not displayed initially. Call pendo.startGuides() to start the Guide sub-system. Default is false.
            // disableGuides: true, // Completely disables the Guides sub-system. Default is false. Set to true to disable guides.
          });
        } catch (e) {
          assert(false, e);
        }
        updatePendo({ profile, authSession, subscriptionTrackingProps, userInfo });
      }
    }
  }, [
    featureFlagsLoadPending,
    featureFlagsError,
    pendoEnabled,
    profile,
    authSession,
    subscriptionTrackingProps,
    userInfo,
  ]);

  useEffect(() => {
    updatePendo({
      profile,
      authSession,
      subscriptionTrackingProps,
      addFiDefaultProvider,
      financialInstitutions,
      currentDataset,
      userInfo,
    });
  }, [
    profile,
    authSession,
    subscriptionTrackingProps,
    addFiDefaultProvider,
    financialInstitutions,
    currentDataset,
    userInfo,
  ]);

  return null;
}

export default Pendo;
